import { Injectable, computed, inject } from '@angular/core';
import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, map, of, switchMap } from 'rxjs';
import { environment } from '../environments/environment';

export type TenantProvisioningStatus =
  | 'pending'
  | 'dns_provisioning'
  | 'database_provisioning'
  | 'migrating'
  | 'seeding'
  | 'deploying'
  | 'ready'
  | 'failed'
  | 'maintenance'
  | 'deprovisioning'
  | 'deprovisioned'
  | 'deprovision_failed';

export interface TenantStatusResponse {
  subdomain: string;
  host: string;
  status: TenantProvisioningStatus;
  statusMessage: string | null;
  seedUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Derives a tenant identifier from the browser hostname.
 *
 * Examples:
 * - acme--develop.smartcapacity.localhost -> acme--develop
 * - acme.smartcapacity.dev -> acme
 */
function deriveTenantFromHost(host: string): string {
  return host.split('.')[0] ?? host;
}

/**
 * Service for checking whether the current tenant is operational.
 *
 * Uses a provisioning-status-first readiness check:
 *
 * 1. **Provisioning status** — Query the app API's provisioning proxy
 *    endpoint (`/api/provisioning/status`). The app API forwards this
 *    to the control plane internally. If a tenant exists and is still
 *    provisioning (pending/migrating/etc.), that status is returned so
 *    the UI can show the provisioning page.
 *
 * 2. **API health check** — For ready/missing control records, probe
 *    the data-plane API (`/api/health`). A successful 200 response means
 *    the tenant is operational.
 *
 * The client never communicates with the control API directly — all
 * requests go through the app API.
 *
 * The `host` property is set when the browser hostname belongs to the
 * configured `tenantDomain` (e.g., `*.smartcapacity.localhost` or
 * `*.smartcapacity.dev`). When `host` is empty (e.g., plain `localhost`
 * in local dev), all checks are skipped and the tenant is assumed ready.
 */
@Injectable({ providedIn: 'root' })
export class TenantStatusService {
  private readonly http = inject(HttpClient);

  /**
   * The full hostname when it belongs to the configured tenant domain,
   * otherwise an empty string (e.g. plain `localhost` in local dev
   * without subdomain).
   */
  readonly host: string = (() => {
    if (typeof window === 'undefined') return '';
    const hostname = window.location.hostname;
    const tenantDomain = environment.tenantDomain;
    if (hostname.endsWith(`.${tenantDomain}`)) return hostname;
    return '';
  })();

  /**
   * Combined resource that resolves provisioning state from the app API's
   * provisioning proxy and operational readiness from the health endpoint.
   */
  readonly statusResource = rxResource<TenantStatusResponse | null, { host: string }>({
    params: () => ({ host: this.host }),
    stream: ({ params }) => {
      if (!params.host) return of(null);

      const subdomain = deriveTenantFromHost(params.host);

      return this.fetchProvisioningStatus().pipe(
        switchMap(controlStatus => {
          // If provisioning status reports a non-ready state, surface it immediately.
          if (controlStatus && controlStatus.status !== 'ready') {
            return of(controlStatus);
          }

          return this.http.get<{ status: string }>('/api/health', { observe: 'response' }).pipe(
            map(response => {
              if (response.status === HttpStatusCode.Ok) {
                return (
                  controlStatus ?? {
                    subdomain,
                    host: params.host,
                    status: 'ready' as const,
                    statusMessage: null,
                    seedUrl: null,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  }
                );
              }

              return controlStatus;
            }),
            catchError(() => of(controlStatus))
          );
        })
      );
    },
  });

  readonly status = computed(() => this.statusResource.value()?.status ?? null);

  /**
   * The tenant's canonical subdomain as returned by the API.
   * Empty string when the status has not been fetched yet or the request failed.
   */
  readonly subdomain = computed(() => this.statusResource.value()?.subdomain ?? '');

  readonly isReady = computed(() => this.status() === 'ready');

  /** Statuses that indicate the tenant has been torn down or is being torn down. */
  private static readonly DEPROVISIONED_STATUSES: ReadonlySet<TenantProvisioningStatus> = new Set([
    'deprovisioning',
    'deprovisioned',
    'deprovision_failed',
  ]);

  /**
   * True when a host is present and the tenant status is an initial-provisioning
   * state (i.e., anything other than 'ready', 'maintenance', or a deprovisioning state).
   *
   * 'maintenance' is excluded because in-place deploy updates have their own
   * dedicated /maintenance route and guard — they must not redirect to /provisioning.
   *
   * Deprovisioning states are excluded because the tenant is being torn down,
   * not provisioned — showing the provisioning page would be incorrect.
   */
  readonly requiresProvisioning = computed(() => {
    if (!this.host) return false;
    const s = this.status();
    return s !== null && s !== 'ready' && s !== 'maintenance' && !TenantStatusService.DEPROVISIONED_STATUSES.has(s);
  });

  /**
   * True when a host is present and the tenant is in maintenance mode
   * (a rolling deploy update is in progress).
   */
  readonly isUnderMaintenance = computed(() => {
    if (!this.host) return false;
    return this.status() === 'maintenance';
  });

  /**
   * True when a host is present and the tenant has been deprovisioned
   * or is being torn down. The UI should show an appropriate error page.
   */
  readonly isDeprovisioned = computed(() => {
    if (!this.host) return false;
    const s = this.status();
    return s !== null && TenantStatusService.DEPROVISIONED_STATUSES.has(s);
  });

  /**
   * Query the app API's provisioning status proxy.
   *
   * The app API forwards this request to the control plane internally,
   * so the client never needs to know the control API's URL.
   *
   * Returns null if the endpoint is unreachable or returns an error.
   */
  private fetchProvisioningStatus() {
    return this.http.get<TenantStatusResponse>('/api/provisioning/status').pipe(catchError(() => of(null)));
  }

  /**
   * Public wrapper around the provisioning-status proxy, used by
   * route components (maintenance, provisioning) that poll the endpoint
   * to drive their progress UIs. Returns `null` on error so callers can
   * render an error state without unwrapping an exception.
   */
  fetchCurrentStatus() {
    return this.fetchProvisioningStatus();
  }
}
